import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MapPin, Phone, Plus, Star, Tag as TagIcon, Trash2 } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'

import {
  CONTACT_KIND_VALUES,
  type PartyAddressSummary,
  type PartyContactSummary,
} from '@aeris/contracts'
import { formatCep } from '@aeris/party'
import { Badge, Button, ConfirmDialog, Input, Select, Spinner } from '@aeris/ui'

import { EMPTY_ADDRESS, type AddressValues } from '../lib/address-values.ts'
import {
  addPartyAddress,
  addPartyContact,
  ApiError,
  fetchParty,
  removePartyAddress,
  setPartyTags,
  removePartyContact,
  updatePartyAddress,
  updatePartyContact,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import {
  ADDRESS_KIND_LABELS,
  CONTACT_KIND_LABELS,
} from '../lib/party-labels.ts'

import { AddressInputs } from './address-inputs.tsx'
import { PartyCreditPanel } from './party-credit-panel.tsx'
import { PartyHistory } from './party-history.tsx'
import { PartyVoucherPanel } from './party-voucher-panel.tsx'
import { TagBadges, TagPicker } from './tag-picker.tsx'

/**
 * Addresses and contacts of one party.
 *
 * Both are lists rather than fixed columns, because Master Specification
 * section 10 lists endereços, contatos, telefones and emails as collections —
 * and a supplier with a switchboard, a WhatsApp for the representative and an
 * email for invoices is the ordinary case, not the elaborate one.
 *
 * Every mutation here bumps `party.version`, because the party is the aggregate
 * root: adding a delivery address IS a change to the registration. That is why
 * this component reports the fresh version upward through `onVersionChange` —
 * without it, the surrounding form would still hold the version it opened with
 * and saving the name afterwards would be refused as a concurrent edit by the
 * very person who caused it.
 */

export type PartyDetailsProps = {
  partyId: string
  canWrite: boolean
  /** Keeps the surrounding form's concurrency token current. See above. */
  onVersionChange: (version: number) => void
}

export const PartyDetails = ({
  partyId,
  canWrite,
  onVersionChange,
}: PartyDetailsProps): ReactNode => {
  const { allows } = useAuth()
  const canManageCredit = allows('finance.credit.manage')
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [addingAddress, setAddingAddress] = useState(false)
  const [addingContact, setAddingContact] = useState(false)
  /*
   * Apagar um endereço ou um telefone era um clique numa lixeira do tamanho de
   * um ícone, sem pergunta e sem volta (F069). O endereço de entrega de um
   * cliente costuma ter sido ditado por telefone uma vez só — quem apaga por
   * engano não tem de onde recuperá-lo.
   */
  const [removendo, setRemovendo] = useState<{
    tipo: 'endereço' | 'contato'
    id: string
    rotulo: string
  } | null>(null)

  /**
   * Marcar NÃO toca `party.version` (F088) — ver a porta `setTags` em
   * `@aeris/party`. Por isso esta mutação não chama `onVersionChange`: o
   * formulário aberto continua valendo, e colar "Atacado" não faz o salvamento
   * de quem estava editando o telefone falhar por conflito.
   */
  const saveTags = useMutation({
    mutationFn: async (tagIds: string[]) => await setPartyTags(partyId, tagIds),
    onSuccess: async () => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['party', partyId] })
      await queryClient.invalidateQueries({ queryKey: ['parties'] })
      // O inbox mostra as mesmos marcadores no cabeçalho da conversa.
      await queryClient.invalidateQueries({
        queryKey: ['communication-conversation-detail'],
      })
    },
    onError: (cause) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar os marcadores.'
      )
    },
  })

  const query = useQuery({
    queryKey: ['party', partyId],
    queryFn: ({ signal }) => fetchParty(partyId, signal),
  })

  const party = query.data?.party
  const version = party?.version

  useEffect(() => {
    if (version !== undefined) onVersionChange(version)
  }, [version, onVersionChange])

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['party', partyId] })
    await queryClient.invalidateQueries({ queryKey: ['parties'] })
  }

  const mutate = useMutation({
    mutationFn: async (action: () => Promise<unknown>) => await action(),
    onSuccess: async () => {
      setError(null)
      setAddingAddress(false)
      setAddingContact(false)
      await refresh()
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível concluir. Tente novamente.'
      )
    },
  })

  if (query.isPending || party === undefined || version === undefined) {
    return (
      <div className="flex justify-center py-6">
        <Spinner />
      </div>
    )
  }

  const busy = mutate.isPending

  const promoteAddress = (address: PartyAddressSummary): void => {
    mutate.mutate(
      async () =>
        await updatePartyAddress(partyId, address.id, {
          version,
          kind: address.kind,
          cep: address.cep ?? '',
          street: address.street,
          number: address.number ?? '',
          complement: address.complement ?? '',
          district: address.district ?? '',
          city: address.city,
          ibgeCode: address.ibgeCode ?? '',
          uf: address.uf,
          country: address.country,
          isMain: true,
        })
    )
  }

  const promoteContact = (contact: PartyContactSummary): void => {
    mutate.mutate(
      async () =>
        await updatePartyContact(partyId, contact.id, {
          version,
          kind: contact.kind,
          value: contact.value,
          label: contact.label ?? '',
          isMain: true,
        })
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {error === null ? null : (
        <p role="alert" className="text-(length:--text-xs) text-(--danger)">
          {error}
        </p>
      )}

      {/* --- Marcadores (F088) --- */}
      <section className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="flex items-center gap-1.5 text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
            <TagIcon aria-hidden="true" className="h-3.5 w-3.5" />
            Marcadores
          </h3>
          {canWrite ? (
            <TagPicker
              scope="party"
              value={party.tags.map((tag) => tag.id)}
              disabled={saveTags.isPending}
              onChange={(tagIds) => {
                saveTags.mutate(tagIds)
              }}
            />
          ) : null}
        </div>

        {party.tags.length === 0 ? (
          <p className="text-(length:--text-xs) text-(--text-muted)">
            Nenhum marcador. Eles seguem esta pessoa em qualquer conversa.
          </p>
        ) : (
          <TagBadges tags={party.tags} />
        )}
      </section>

      {/* Credit first: it is the fact that decides whether this customer can
          buy at all today, and it belongs above the addresses rather than
          below two lists somebody has to scroll past. */}
      <PartyCreditPanel partyId={partyId} canManage={canManageCredit} />

      {/* O vale é o contrário do crédito acima: não o quanto o cliente pode
          dever, e sim o quanto a loja deve a ele (F126). Ao lado, e com o
          título dizendo isso, para "saldo" não ser lido dos dois jeitos. */}
      {allows('exchange.read') ? (
        <PartyVoucherPanel partyId={partyId} customerName={party.legalName} />
      ) : null}

      {/* Compras e títulos em aberto, na ficha (F132). */}
      <PartyHistory partyId={partyId} />

      {/* --- Addresses --- */}
      <section className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="flex items-center gap-1.5 text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
            <MapPin aria-hidden="true" className="h-3.5 w-3.5" />
            Endereços
          </h3>
          {canWrite && !addingAddress ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setAddingAddress(true)
              }}
              leadingIcon={<Plus aria-hidden="true" className="h-3.5 w-3.5" />}
            >
              Adicionar endereço
            </Button>
          ) : null}
        </div>

        {party.addresses.length === 0 && !addingAddress ? (
          <p className="text-(length:--text-xs) text-(--text-muted)">
            Nenhum endereço cadastrado.
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {party.addresses.map((address) => (
              <li
                key={address.id}
                className="flex items-center gap-2 rounded-(--radius-md) border border-(--border-subtle) px-2 py-1.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-(length:--text-sm) text-(--text-primary)">
                    {address.street}
                    {address.number === null ? '' : `, ${address.number}`}
                    {address.district === null ? '' : ` — ${address.district}`}
                  </p>
                  <p className="truncate text-(length:--text-xs) text-(--text-muted)">
                    {address.city}/{address.uf}
                    {address.cep === null
                      ? ''
                      : ` · CEP ${formatCep(address.cep)}`}{' '}
                    · {ADDRESS_KIND_LABELS[address.kind]}
                  </p>
                </div>
                {address.isMain ? (
                  <Badge tone="info">Principal</Badge>
                ) : canWrite ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Tornar ${address.street} o endereço principal`}
                    disabled={busy}
                    onClick={() => {
                      promoteAddress(address)
                    }}
                  >
                    <Star aria-hidden="true" className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
                {canWrite ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remover o endereço ${address.street}`}
                    disabled={busy}
                    onClick={() => {
                      setRemovendo({
                        tipo: 'endereço',
                        id: address.id,
                        rotulo: address.street,
                      })
                    }}
                  >
                    <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {addingAddress ? (
          <AddressFields
            busy={busy}
            onCancel={() => {
              setAddingAddress(false)
            }}
            onSave={(values) => {
              mutate.mutate(
                async () =>
                  await addPartyAddress(partyId, { version, ...values })
              )
            }}
          />
        ) : null}
      </section>

      {/* --- Contacts --- */}
      <section className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="flex items-center gap-1.5 text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
            <Phone aria-hidden="true" className="h-3.5 w-3.5" />
            Contatos
          </h3>
          {canWrite && !addingContact ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setAddingContact(true)
              }}
              leadingIcon={<Plus aria-hidden="true" className="h-3.5 w-3.5" />}
            >
              Adicionar contato
            </Button>
          ) : null}
        </div>

        {party.contacts.length === 0 && !addingContact ? (
          <p className="text-(length:--text-xs) text-(--text-muted)">
            Nenhum contato cadastrado.
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {party.contacts.map((contact) => (
              <li
                key={contact.id}
                className="flex items-center gap-2 rounded-(--radius-md) border border-(--border-subtle) px-2 py-1.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-(length:--text-sm) text-(--text-primary)">
                    {contact.value}
                  </p>
                  <p className="truncate text-(length:--text-xs) text-(--text-muted)">
                    {CONTACT_KIND_LABELS[contact.kind]}
                    {contact.label === null ? '' : ` · ${contact.label}`}
                  </p>
                </div>
                {contact.isMain ? (
                  <Badge tone="info">Principal</Badge>
                ) : canWrite ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Tornar ${contact.value} o contato principal`}
                    disabled={busy}
                    onClick={() => {
                      promoteContact(contact)
                    }}
                  >
                    <Star aria-hidden="true" className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
                {canWrite ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remover o contato ${contact.value}`}
                    disabled={busy}
                    onClick={() => {
                      setRemovendo({
                        tipo: 'contato',
                        id: contact.id,
                        rotulo: contact.value,
                      })
                    }}
                  >
                    <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {addingContact ? (
          <ContactFields
            busy={busy}
            onCancel={() => {
              setAddingContact(false)
            }}
            onSave={(values) => {
              mutate.mutate(
                async () =>
                  await addPartyContact(partyId, { version, ...values })
              )
            }}
          />
        ) : null}
      </section>

      <ConfirmDialog
        open={removendo !== null}
        onOpenChange={(aberto) => {
          if (!aberto) setRemovendo(null)
        }}
        title={removendo === null ? '' : `Remover este ${removendo.tipo}?`}
        description={
          removendo === null
            ? ''
            : `“${removendo.rotulo}” sai do cadastro desta pessoa e não volta. Os documentos já emitidos guardam o que foi usado neles e não mudam.`
        }
        confirmLabel={removendo === null ? '' : `Remover o ${removendo.tipo}`}
        loading={busy}
        onConfirm={() => {
          const alvo = removendo
          setRemovendo(null)
          // `version` já está estreitado aqui: este trecho só existe depois do
          // retorno antecipado de quando a pessoa ainda não carregou.
          if (alvo === null) return
          mutate.mutate(async () =>
            alvo.tipo === 'endereço'
              ? await removePartyAddress(partyId, alvo.id, version)
              : await removePartyContact(partyId, alvo.id, version)
          )
        }}
      />
    </div>
  )
}

/**
 * Um endereço novo num cadastro que já existe. Os campos, com a busca de CEP,
 * moram em `address-inputs.tsx` — o "Novo cadastro" usa os mesmos (F130).
 */
const AddressFields = ({
  busy,
  onCancel,
  onSave,
}: {
  busy: boolean
  onCancel: () => void
  onSave: (values: AddressValues) => void
}): ReactNode => {
  const [values, setValues] = useState<AddressValues>(EMPTY_ADDRESS)

  return (
    <div className="flex flex-col gap-2 rounded-(--radius-md) border border-dashed border-(--border-default) p-2">
      <AddressInputs values={values} onChange={setValues} />

      <div className="flex items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-(length:--text-xs) text-(--text-secondary)">
          <input
            type="checkbox"
            checked={values.isMain}
            onChange={(event) => {
              setValues((current) => ({
                ...current,
                isMain: event.target.checked,
              }))
            }}
            className="h-4 w-4 accent-(--brand-primary)"
          />
          Endereço principal
        </label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onCancel}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            loading={busy}
            onClick={() => {
              onSave(values)
            }}
          >
            Salvar endereço
          </Button>
        </div>
      </div>
    </div>
  )
}

type ContactValues = {
  kind: (typeof CONTACT_KIND_VALUES)[number]
  value: string
  label: string
  isMain: boolean
}

const ContactFields = ({
  busy,
  onCancel,
  onSave,
}: {
  busy: boolean
  onCancel: () => void
  onSave: (values: ContactValues) => void
}): ReactNode => {
  const [values, setValues] = useState<ContactValues>({
    kind: 'phone',
    value: '',
    label: '',
    isMain: false,
  })

  return (
    <div className="flex flex-col gap-2 rounded-(--radius-md) border border-dashed border-(--border-default) p-2">
      <div className="grid gap-2 sm:grid-cols-3">
        <Select
          value={values.kind}
          onChange={(event) => {
            setValues((current) => ({
              ...current,
              kind: event.target.value as ContactValues['kind'],
            }))
          }}
          aria-label="Tipo de contato"
        >
          {CONTACT_KIND_VALUES.map((kind) => (
            <option key={kind} value={kind}>
              {CONTACT_KIND_LABELS[kind]}
            </option>
          ))}
        </Select>
        <Input
          value={values.value}
          onChange={(event) => {
            setValues((current) => ({ ...current, value: event.target.value }))
          }}
          placeholder={
            values.kind === 'email'
              ? 'compras@empresa.com.br'
              : '(11) 98765-4321'
          }
          aria-label="Contato"
        />
        <Input
          value={values.label}
          onChange={(event) => {
            setValues((current) => ({ ...current, label: event.target.value }))
          }}
          placeholder="Identificação (Financeiro, Compras…)"
          aria-label="Identificação do contato"
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-(length:--text-xs) text-(--text-secondary)">
          <input
            type="checkbox"
            checked={values.isMain}
            onChange={(event) => {
              setValues((current) => ({
                ...current,
                isMain: event.target.checked,
              }))
            }}
            className="h-4 w-4 accent-(--brand-primary)"
          />
          Contato principal
        </label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onCancel}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            loading={busy}
            onClick={() => {
              onSave(values)
            }}
          >
            Salvar contato
          </Button>
        </div>
      </div>
    </div>
  )
}
